import { db } from '../firebase';
import { 
  collection, 
  addDoc, 
  getDocs, 
  doc, 
  getDoc, 
  updateDoc, 
  deleteDoc, 
  query, 
  where, 
  orderBy, 
  limit, 
  startAfter, 
  serverTimestamp, 
  onSnapshot,
  writeBatch,
  runTransaction
} from 'firebase/firestore';
import { auth } from '../firebase';

// Collection references
const parentOrdersCollection = collection(db, 'parent_orders');
const merchantOrdersCollection = collection(db, 'merchant_orders');
const orderItemsCollection = collection(db, 'order_items');
const shopsCollection = collection(db, 'shops');

// Helper function to clean data for Firestore (replace undefined with null)
function cleanForFirestore(obj) {
  if (obj === null || obj === undefined) return null;
  if (typeof obj !== 'object') return obj;
  
  // Preserve server timestamps
  if (obj._methodName === 'serverTimestamp') {
    return obj;
  }
  
  const cleaned = {};
  for (const [key, value] of Object.entries(obj)) {
    if (value === undefined) {
      cleaned[key] = null;
    } else if (typeof value === 'object' && value !== null) {
      // Preserve server timestamps
      if (value._methodName === 'serverTimestamp') {
        cleaned[key] = value;
      } else {
        cleaned[key] = cleanForFirestore(value);
      }
    } else {
      cleaned[key] = value;
    }
  }
  return cleaned;
}

// Helper function to fetch shop names by seller IDs
async function getShopNamesBySellerIds(sellerIds) {
  try {
    if (!sellerIds || sellerIds.length === 0) return {};
    
    // Remove duplicates
    const uniqueSellerIds = [...new Set(sellerIds)];
    console.log('🔍 Unique seller IDs for query:', uniqueSellerIds);
    console.log('🔍 First unique seller ID length:', uniqueSellerIds[0]?.length);
    
    // Query shops by sellerId (not userId)
    const shopsQuery = query(
      shopsCollection,
      where('sellerId', 'in', uniqueSellerIds)
    );
    
    const shopsSnapshot = await getDocs(shopsQuery);
    console.log('🔍 Shops query result - empty?', shopsSnapshot.empty);
    console.log('🔍 Number of shops found:', shopsSnapshot.docs.length);
    
    const shopNamesMap = {};
    
    shopsSnapshot.docs.forEach(doc => {
      const shopData = doc.data();
      console.log('🏪 Shop document data:', { id: doc.id, sellerId: shopData.sellerId, name: shopData.name });
      shopNamesMap[shopData.sellerId] = shopData.name || shopData.shopName || shopData.displayName || `Shop ${shopData.sellerId?.slice(-4)}`;
    });
    
    console.log('🏪 Fetched shop names for sellers:', shopNamesMap);
    return shopNamesMap;
  } catch (error) {
    console.error('❌ Error fetching shop names:', error);
    return {};
  }
}

// Create a new parent order (umbrella for buyer, one payment)
export async function createParentOrder(orderData) {
  try {
    const user = auth.currentUser;
    if (!user) throw new Error('Not authenticated');

    const parentOrder = {
      id: orderData.id || null, // Allow custom ID for payment integration
      buyerId: user.uid, // Use buyerId for security rules
      userId: user.uid, // Keep for backward compatibility
      status: 'pending', // PRD: Start in pending state, awaiting seller acceptance
      totals: {
        subtotal: orderData.subtotal || 0,
        fees: orderData.fees || 0,
        tax: orderData.tax || 0,
        total: orderData.total || 0,
        shipping: orderData.shipping || 0,
        serviceFee: orderData.serviceFee || 0, // Vesta's $2 service fee
        deliveryFee: orderData.deliveryFee || 0, // Delivery fee (if any)
      },
      // PRD-required breakdown for transparency
      breakdown: {
        grossOrderAmount: orderData.total || 0, // Total customer paid
        productSubtotal: orderData.subtotal || 0, // Product prices only
        deliveryFee: orderData.deliveryFee || 0, // Delivery charges
        tax: orderData.tax || 0, // Sales tax
        stripeFees: 0, // Will be calculated after payment (2.9% + $0.30)
        vestaFee: orderData.serviceFee || 2.0, // $2 flat fee
        netSellerPayout: 0, // Will be calculated: gross - stripeFees - vestaFee
        platformRevenue: orderData.serviceFee || 2.0, // Vesta's revenue
      },
      // PRD: Order state tracking
      orderStates: {
        current: 'pending',
        history: [{
          state: 'pending',
          timestamp: serverTimestamp(),
          triggeredBy: 'system',
          notes: 'Order placed, awaiting seller acceptance'
        }]
      },
      paymentIntentId: orderData.paymentIntentId,
      billingDetails: orderData.billingDetails,
      shippingAddress: orderData.shippingAddress,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    };

    console.log('Creating parent order:', {
      buyerId: parentOrder.buyerId,
      total: parentOrder.totals.total,
      items: orderData.items?.length || 0
    });

    const docRef = await addDoc(parentOrdersCollection, cleanForFirestore(parentOrder));
    return { ...parentOrder, id: docRef.id };
  } catch (error) {
    console.error('Error creating parent order:', error);
    throw error;
  }
}

// Create merchant orders for each seller
export async function createMerchantOrders(parentOrderId, items) {
  try {
    const user = auth.currentUser;
    if (!user) throw new Error('Not authenticated');

    // Group items by seller - get sellerId from product data
    const itemsBySeller = {};
    for (const item of items) {
      let sellerId = item.sellerId || item.creatorId;
      
      // If no sellerId in item, look it up from the product
      if (!sellerId) {
        try {
          // Use productId if available (for variant items), otherwise fall back to id
          const productId = item.productId || item.id;
          const productRef = doc(db, 'products', productId);
          const productSnap = await getDoc(productRef);
          if (productSnap.exists()) {
            sellerId = productSnap.data().sellerId;
          }
        } catch (error) {
          console.error('Error looking up product sellerId:', error);
          continue; // Skip this item if we can't determine the seller
        }
      }
      
      if (!sellerId) {
        console.error('No sellerId found for item:', item.id);
        continue; // Skip items without sellerId
      }
      
      if (!itemsBySeller[sellerId]) {
        itemsBySeller[sellerId] = [];
      }
      itemsBySeller[sellerId].push(item);
    }

    const merchantOrders = [];
    const orderItems = [];

    // Create merchant order for each seller
    for (const [sellerId, sellerItems] of Object.entries(itemsBySeller)) {
      // Calculate totals for this seller (amounts are already in cents from payment service)
      const subtotal = sellerItems.reduce((sum, item) => sum + (parseFloat(item.price) * (item.qty || 1)), 0);
      const tax = Math.round(subtotal * 0.08); // 8% tax in cents
      const total = subtotal + tax;

      // Create merchant order
      const merchantOrder = {
        parentOrderId,
        sellerId,
        buyerId: user.uid, // Add buyer ID for security rules
        status: 'placed',
        totals: {
          subtotal: Math.round(subtotal),
          tax: Math.round(tax),
          total: Math.round(total)
        },
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      };

      const merchantOrderRef = await addDoc(merchantOrdersCollection, cleanForFirestore(merchantOrder));
      merchantOrders.push({ id: merchantOrderRef.id, ...merchantOrder });

      // Create order items for this seller
      for (const item of sellerItems) {
        const orderItem = {
          merchantOrderId: merchantOrderRef.id,
          productId: item.productId || item.id, // Use productId if available, fallback to id
          productSnapshot: {
            name: item.name,
            variant: item.variant || null,
            image: item.image,
            description: item.description || null
          },
          quantity: item.qty || 1,
          unitPrice: item.price,
          // Prioritize pickupDate, then fulfillmentDay, then serviceDate
          // This ensures we get the correct date from the cart item
          serviceDate: item.pickupDate || item.fulfillmentDay || item.serviceDate || new Date().toISOString().split('T')[0],
          windowStartLocal: item.windowStart || '17:00',
          windowEndLocal: item.windowEnd || '19:00',
          status: 'placed',
          buyerId: user.uid, // Add buyer ID for security rules
          sellerId: sellerId, // Use the sellerId we determined from product lookup
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp()
        };

        const orderItemRef = await addDoc(orderItemsCollection, cleanForFirestore(orderItem));
        orderItems.push({ id: orderItemRef.id, ...orderItem });
      }
    }

    return { merchantOrders, orderItems };
  } catch (error) {
    console.error('Error creating merchant orders:', error);
    throw error;
  }
}

// Get parent order by payment intent ID
export async function getParentOrderByPaymentIntent(paymentIntentId) {
  try {
    const user = auth.currentUser;
    if (!user) throw new Error('Not authenticated');

    // Query parent orders by payment intent ID
    const parentOrdersQuery = query(
      parentOrdersCollection,
      where('paymentIntentId', '==', paymentIntentId),
      where('buyerId', '==', user.uid)
    );
    
    const parentOrdersSnapshot = await getDocs(parentOrdersQuery);
    
    if (parentOrdersSnapshot.empty) {
      throw new Error('Parent order not found for payment intent');
    }

    const parentOrderDoc = parentOrdersSnapshot.docs[0];
    const parentOrder = { id: parentOrderDoc.id, ...parentOrderDoc.data() };

    // Get merchant orders for this parent order
    let merchantOrders = [];
    try {
      const merchantOrdersQuery = query(
        merchantOrdersCollection,
        where('parentOrderId', '==', parentOrder.id)
      );
      
      const merchantOrdersSnap = await getDocs(merchantOrdersQuery);
      
      merchantOrders = merchantOrdersSnap.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
    } catch (merchantError) {
      console.error('Error querying merchant orders for payment intent:', merchantError);
      // Return just the parent order if merchant orders query fails
      return {
        parentOrder,
        merchantOrders: []
      };
    }

    // Get order items for all merchant orders
    const merchantOrderIds = merchantOrders.map(mo => mo.id);
    
    let orderItems = [];
    if (merchantOrderIds.length > 0) {
      const orderItemsQuery = query(
        orderItemsCollection,
        where('merchantOrderId', 'in', merchantOrderIds)
      );
      const orderItemsSnap = await getDocs(orderItemsQuery);
      
      orderItems = orderItemsSnap.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      console.log('🔍 [getParentOrderByPaymentIntent] Found order items:', orderItems.length);
    } else {
      console.log('🔍 [getParentOrderByPaymentIntent] No merchant orders found, skipping order items query');
    }

    // Group items by merchant order
    const itemsByMerchantOrder = {};
    orderItems.forEach(item => {
      if (!itemsByMerchantOrder[item.merchantOrderId]) {
        itemsByMerchantOrder[item.merchantOrderId] = [];
      }
      itemsByMerchantOrder[item.merchantOrderId].push(item);
    });

    // Attach items to merchant orders
    merchantOrders.forEach(merchantOrder => {
      merchantOrder.items = itemsByMerchantOrder[merchantOrder.id] || [];
    });

    return {
      parentOrder,
      merchantOrders
    };
  } catch (error) {
    console.error('Error getting parent order by payment intent:', error);
    throw error;
  }
}

// Get parent order with all merchant orders and items
export async function getParentOrderWithDetails(parentOrderId) {
  try {
    const user = auth.currentUser;
    if (!user) throw new Error('Not authenticated');

    // First, try to get merchant orders for this parent order to determine user's role
    const merchantOrdersQuery = query(
      merchantOrdersCollection,
      where('parentOrderId', '==', parentOrderId)
    );
    const merchantOrdersSnap = await getDocs(merchantOrdersQuery);
    
    if (merchantOrdersSnap.empty) {
      throw new Error('No merchant orders found for this parent order');
    }

    const merchantOrders = merchantOrdersSnap.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));

    // Check if user is involved in any of these merchant orders
    const userMerchantOrder = merchantOrders.find(mo => 
      mo.buyerId === user.uid || mo.sellerId === user.uid
    );

    if (!userMerchantOrder) {
      throw new Error('Insufficient permissions to access this order');
    }

    // Determine if user is buyer or seller
    const isBuyer = userMerchantOrder.buyerId === user.uid;
    const isSeller = userMerchantOrder.sellerId === user.uid;

    let parentOrder;
    
    if (isBuyer) {
      // Buyers can read parent orders directly
      const parentOrderRef = doc(db, 'parent_orders', parentOrderId);
      const parentOrderSnap = await getDoc(parentOrderRef);
      
      if (!parentOrderSnap.exists()) {
        throw new Error('Parent order not found');
      }

      parentOrder = { id: parentOrderSnap.id, ...parentOrderSnap.data() };
    } else if (isSeller) {
      // Sellers cannot read parent orders directly, so construct minimal parent order data
      // from the merchant order data they have access to
      const firstMerchantOrder = merchantOrders.find(mo => mo.sellerId === user.uid);
      parentOrder = {
        id: parentOrderId,
        buyerId: firstMerchantOrder.buyerId,
        createdAt: firstMerchantOrder.createdAt,
        // Add other fields that sellers might need, but don't include sensitive buyer data
        totals: {
          subtotal: 0, // Will be calculated from merchant orders
          tax: 0,
          fees: 0,
          total: 0
        }
      };
    }

    // Filter merchant orders based on user role
    let accessibleMerchantOrders = merchantOrders;
    
    if (isSeller) {
      // Sellers can only see their own merchant orders
      accessibleMerchantOrders = merchantOrders.filter(mo => mo.sellerId === user.uid);
    }
    // Buyers can see all merchant orders in the parent order

    // Get order items for accessible merchant orders
    let orderItems = [];
    try {
      const merchantOrderIds = accessibleMerchantOrders.map(mo => mo.id);
      
      // Skip order items query if no merchant orders found
      if (merchantOrderIds.length === 0) {
        orderItems = [];
      } else {
        const orderItemsQuery = query(
          orderItemsCollection,
          where('merchantOrderId', 'in', merchantOrderIds)
        );
        const orderItemsSnap = await getDocs(orderItemsQuery);
        
        orderItems = orderItemsSnap.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
      }
    } catch (orderItemsError) {
      console.error('Error querying order items:', orderItemsError);
      throw orderItemsError;
    }

    // Group items by merchant order
    const itemsByMerchantOrder = {};
    orderItems.forEach(item => {
      if (!itemsByMerchantOrder[item.merchantOrderId]) {
        itemsByMerchantOrder[item.merchantOrderId] = [];
      }
      itemsByMerchantOrder[item.merchantOrderId].push(item);
    });

    // Attach items to merchant orders
    accessibleMerchantOrders.forEach(merchantOrder => {
      merchantOrder.items = itemsByMerchantOrder[merchantOrder.id] || [];
    });

    return {
      parentOrder,
      merchantOrders: accessibleMerchantOrders
    };
  } catch (error) {
    console.error('Error getting parent order with details:', error);
    
    // Fallback to legacy orders if multi-vendor order fails
    try {
      const user = auth.currentUser;
      if (!user) throw new Error('Not authenticated');
      
      // Try to get legacy order
      const legacyOrderRef = doc(db, 'orders', parentOrderId);
      const legacyOrderSnap = await getDoc(legacyOrderRef);
      
      if (!legacyOrderSnap.exists()) {
        throw new Error('Order not found in legacy collection either');
      }
      
      const legacyOrder = { id: legacyOrderSnap.id, ...legacyOrderSnap.data() };
      
      // Check if user has permission to access this legacy order
      if (legacyOrder.userId !== user.uid && legacyOrder.sellerId !== user.uid) {
        throw new Error('Insufficient permissions to access this order');
      }
      
      // Transform legacy order to multi-vendor format
      const transformedOrder = {
        parentOrder: {
          id: legacyOrder.id,
          buyerId: legacyOrder.userId,
          sellerId: legacyOrder.sellerId,
          createdAt: legacyOrder.createdAt,
          updatedAt: legacyOrder.updatedAt,
          status: legacyOrder.status,
          totals: {
            subtotal: legacyOrder.subtotal || 0,
            tax: legacyOrder.tax || 0,
            total: legacyOrder.total || 0
          },
          paymentIntentId: legacyOrder.paymentIntentId,
          billingDetails: legacyOrder.billingDetails,
          shippingAddress: legacyOrder.shippingAddress
        },
        merchantOrders: [{
          id: legacyOrder.id,
          parentOrderId: legacyOrder.id,
          sellerId: legacyOrder.sellerId,
          sellerName: legacyOrder.sellerName || `Seller ${legacyOrder.sellerId?.slice(-4) || 'Unknown'}`,
          buyerId: legacyOrder.userId,
          createdAt: legacyOrder.createdAt,
          status: legacyOrder.status,
          totals: {
            subtotal: legacyOrder.subtotal || 0,
            tax: legacyOrder.tax || 0,
            total: legacyOrder.total || 0
          },
          items: legacyOrder.items?.map((item, index) => ({
            id: `${legacyOrder.id}_item_${index}`,
            merchantOrderId: legacyOrder.id,
            productId: item.id || item.productId,
            productSnapshot: {
              name: item.name,
              image: item.image,
              price: item.price,
              description: item.description
            },
            quantity: item.quantity || item.qty || 1,
            unitPrice: (item.price || 0) * 100,
            serviceDate: item.fulfillmentDay || new Date().toISOString().split('T')[0],
            windowStartLocal: '12:00',
            windowEndLocal: '18:00',
            status: legacyOrder.status || 'placed',
            buyerId: legacyOrder.userId,
            sellerId: legacyOrder.sellerId
          })) || []
        }]
      };
      
      return transformedOrder;
      
    } catch (fallbackError) {
      console.error('Fallback to legacy orders also failed:', fallbackError);
      throw error; // Throw the original error
    }
  }
}

// Get merchant orders for a seller
export async function getSellerMerchantOrders(sellerId = null) {
  try {
    const user = auth.currentUser;
    if (!user) throw new Error('Not authenticated');

    const targetSellerId = sellerId || user.uid;

    let merchantOrders = [];
    
    // First, try to get merchant orders for this seller
    try {
      const merchantOrdersQuery = query(
        merchantOrdersCollection,
        where('sellerId', '==', targetSellerId),
        orderBy('createdAt', 'desc')
      );
      
      const snapshot = await getDocs(merchantOrdersQuery);
      
      for (const doc of snapshot.docs) {
        const merchantOrder = { id: doc.id, ...doc.data() };
        
        // Get items for this merchant order
        const orderItemsQuery = query(
          orderItemsCollection,
          where('merchantOrderId', '==', doc.id),
          orderBy('serviceDate', 'asc')
        );
        
        const itemsSnapshot = await getDocs(orderItemsQuery);
        merchantOrder.items = itemsSnapshot.docs.map(itemDoc => ({
          id: itemDoc.id,
          ...itemDoc.data()
        }));
        
        merchantOrders.push(merchantOrder);
      }
    } catch (error) {
      console.log('No merchant orders found, falling back to legacy orders:', error.message);
      
      // Fallback to legacy orders if no merchant orders exist
      const legacyOrdersQuery = query(
        collection(db, 'orders'),
        where('sellerId', '==', targetSellerId),
        orderBy('createdAt', 'desc')
      );
      
      const legacyOrdersSnapshot = await getDocs(legacyOrdersQuery);
      
      merchantOrders = legacyOrdersSnapshot.docs.map(orderDoc => {
        const orderData = orderDoc.data();
        return {
          id: orderDoc.id,
          sellerId: orderData.sellerId,
          sellerName: orderData.sellerName || `Seller ${orderData.sellerId?.slice(-4) || 'Unknown'}`,
          parentOrderId: orderDoc.id,
          createdAt: orderData.createdAt,
          status: orderData.status,
          totals: {
            subtotal: orderData.subtotal || 0,
            tax: orderData.tax || 0,
            total: orderData.total || 0
          },
          items: orderData.items?.map((item, index) => ({
            id: `${orderDoc.id}_item_${index}`,
            merchantOrderId: orderDoc.id,
            productId: item.id || item.productId,
            productSnapshot: {
              name: item.name,
              image: item.image,
              price: item.price
            },
            quantity: item.quantity || item.qty || 1,
            unitPrice: item.price * 100,
            serviceDate: item.fulfillmentDay || new Date().toISOString().split('T')[0],
            windowStartLocal: '12:00',
            windowEndLocal: '14:00',
            status: orderData.status === 'completed' ? 'completed' : 'placed',
            buyerId: orderData.userId,
            sellerId: orderData.sellerId
          })) || []
        };
      });
    }

    return merchantOrders;
  } catch (error) {
    console.error('Error getting seller merchant orders:', error);
    throw error;
  }
}

// Get parent orders for a buyer
export async function getBuyerParentOrders(userId = null, limitCount = null, offset = 0) {
  try {
    const user = auth.currentUser;
    if (!user) throw new Error('Not authenticated');

    const targetUserId = userId || user.uid;
    let parentOrders = [];
    
    // First, try to get parent orders for this buyer
    try {
      let parentOrdersQuery = query(
        parentOrdersCollection,
        where('buyerId', '==', targetUserId),
        orderBy('createdAt', 'desc')
      );
      
      // Add pagination if specified
      if (limitCount) {
        parentOrdersQuery = query(parentOrdersQuery, limit(limitCount));
      }
      
      const snapshot = await getDocs(parentOrdersQuery);
      
      for (const doc of snapshot.docs) {
        try {
          // Ensure we preserve the document ID and don't let any data field override it
          const documentData = doc.data();
          const parentOrder = { 
            id: doc.id, // Always use the document ID
            ...documentData,
            // Ensure id field is not overridden by any data field
            id: doc.id
          };
          
          // Skip parent orders that have payment intent IDs as document IDs
          if (doc.id.startsWith('pi_')) {
            continue;
          }
          
          // Skip parent orders that don't look like valid Firestore document IDs
          if (doc.id.length < 20) {
            continue;
          }
          
          // Get merchant orders for this parent order
          const merchantOrdersQuery = query(
            merchantOrdersCollection,
            where('parentOrderId', '==', doc.id)
          );
          
          try {
            const merchantOrdersSnapshot = await getDocs(merchantOrdersQuery);
            const merchantOrders = [];
            
            for (const moDoc of merchantOrdersSnapshot.docs) {
              try {
                const merchantOrder = { id: moDoc.id, ...moDoc.data() };
                
                // Get items for this merchant order
                const orderItemsQuery = query(
                  orderItemsCollection,
                  where('merchantOrderId', '==', moDoc.id),
                  orderBy('serviceDate', 'asc')
                );
                
                const itemsSnapshot = await getDocs(orderItemsQuery);
                merchantOrder.items = itemsSnapshot.docs.map(itemDoc => ({
                  id: itemDoc.id,
                  ...itemDoc.data()
                }));
                
                merchantOrders.push(merchantOrder);
              } catch (merchantError) {
                console.error('Error processing merchant order:', moDoc.id, merchantError);
                // Continue with other merchant orders
              }
            }
            
            parentOrder.merchantOrders = merchantOrders;
            parentOrders.push(parentOrder);
          } catch (merchantQueryError) {
            console.error('Error querying merchant orders for parent order:', doc.id, merchantQueryError);
            // Still add the parent order even if merchant orders can't be loaded
            parentOrder.merchantOrders = [];
            parentOrders.push(parentOrder);
          }
        } catch (parentOrderError) {
          console.error('🔍 [getBuyerParentOrders] Error processing parent order:', doc.id, parentOrderError);
          // Continue with other parent orders
        }
      }
    } catch (error) {
      console.log('🔍 [getBuyerParentOrders] No parent orders found, falling back to legacy orders:', error.message);
      
      try {
        // Fallback to legacy orders if no parent orders exist
        const legacyOrdersQuery = query(
          collection(db, 'orders'),
          where('userId', '==', targetUserId),
          orderBy('createdAt', 'desc')
        );
        
        const legacyOrdersSnapshot = await getDocs(legacyOrdersQuery);
        console.log('🔍 [getBuyerParentOrders] Found legacy orders:', legacyOrdersSnapshot.docs.length);
        
        parentOrders = legacyOrdersSnapshot.docs.map(orderDoc => {
        const orderData = orderDoc.data();
        return {
          id: orderDoc.id,
          parentOrder: {
            id: orderDoc.id,
            buyerId: orderData.userId,
            createdAt: orderData.createdAt,
            status: orderData.status,
            totals: {
              subtotal: orderData.subtotal || 0,
              tax: orderData.tax || 0,
              shipping: orderData.shipping || 0,
              total: orderData.total || 0
            },
            billingDetails: orderData.billingDetails,
            shippingAddress: orderData.shippingAddress
          },
          merchantOrders: [{
            id: orderDoc.id,
            sellerId: orderData.sellerId,
            sellerName: orderData.sellerName || `Seller ${orderData.sellerId?.slice(-4) || 'Unknown'}`,
            parentOrderId: orderDoc.id,
            createdAt: orderData.createdAt,
            status: orderData.status,
            items: orderData.items?.map((item, index) => ({
              id: `${orderDoc.id}_item_${index}`,
              merchantOrderId: orderDoc.id,
              productId: item.id || item.productId,
              productSnapshot: {
                name: item.name,
                image: item.image,
                price: item.price
              },
              quantity: item.quantity || item.qty || 1,
              unitPrice: item.price * 100,
              serviceDate: item.fulfillmentDay || new Date().toISOString().split('T')[0],
              windowStartLocal: '12:00',
              windowEndLocal: '14:00',
              status: orderData.status === 'completed' ? 'completed' : 'placed',
              buyerId: orderData.userId,
              sellerId: orderData.sellerId
            })) || []
          }]
        };
      });
      } catch (legacyError) {
        console.error('🔍 [getBuyerParentOrders] Error fetching legacy orders:', legacyError);
        // Return empty array if both new and legacy orders fail
        return [];
      }
    }

    console.log('🔍 [getBuyerParentOrders] Returning orders:', parentOrders.length);
    return parentOrders;
  } catch (error) {
    console.error('Error getting buyer parent orders:', error);
    throw error;
  }
}

// Update order item status
export async function updateOrderItemStatus(itemId, status) {
  try {
    const user = auth.currentUser;
    if (!user) throw new Error('Not authenticated');

    const itemRef = doc(db, 'order_items', itemId);
    const itemSnap = await getDoc(itemRef);
    
    if (!itemSnap.exists()) {
      throw new Error('Order item not found');
    }

    const itemData = itemSnap.data();
    
    // Verify user is the seller
    const merchantOrderRef = doc(db, 'merchant_orders', itemData.merchantOrderId);
    const merchantOrderSnap = await getDoc(merchantOrderRef);
    
    if (!merchantOrderSnap.exists()) {
      throw new Error('Merchant order not found');
    }

    const merchantOrderData = merchantOrderSnap.data();
    if (merchantOrderData.sellerId !== user.uid) {
      throw new Error('Not authorized to update this order');
    }

    // Update item status
    await updateDoc(itemRef, {
      status,
      updatedAt: serverTimestamp()
    });

    // Update merchant order status based on item statuses
    await updateMerchantOrderStatus(itemData.merchantOrderId);

    return { success: true, itemId, status };
  } catch (error) {
    console.error('Error updating order item status:', error);
    throw error;
  }
}

// Update merchant order status based on item statuses
export async function updateMerchantOrderStatus(merchantOrderId) {
  try {
    // Get all items for this merchant order
    const orderItemsQuery = query(
      orderItemsCollection,
      where('merchantOrderId', '==', merchantOrderId)
    );
    
    const itemsSnapshot = await getDocs(orderItemsQuery);
    const items = itemsSnapshot.docs.map(doc => doc.data());
    
    if (items.length === 0) return;

    // Calculate aggregate status
    const statusCounts = {};
    items.forEach(item => {
      statusCounts[item.status] = (statusCounts[item.status] || 0) + 1;
    });

    let aggregateStatus = 'placed';
    
    if (statusCounts.completed === items.length) {
      aggregateStatus = 'completed';
    } else if (statusCounts.completed > 0) {
      aggregateStatus = 'partially_completed';
    } else if (statusCounts.ready > 0) {
      aggregateStatus = 'partially_ready';
    } else if (statusCounts.cancelled === items.length) {
      aggregateStatus = 'cancelled';
    }

    // Update merchant order status
    const merchantOrderRef = doc(db, 'merchant_orders', merchantOrderId);
    await updateDoc(merchantOrderRef, {
      status: aggregateStatus,
      updatedAt: serverTimestamp()
    });

    return { success: true, merchantOrderId, status: aggregateStatus };
  } catch (error) {
    console.error('Error updating merchant order status:', error);
    throw error;
  }
}

// Get order items directly from order_items collection for a parent order
export async function getOrderItemsForParentOrder(parentOrderId, userId) {
  try {
    // First, try to get the merchant orders for this parent order
    const merchantOrdersQuery = query(
      merchantOrdersCollection,
      where('parentOrderId', '==', parentOrderId),
      where('buyerId', '==', userId)
    );
    
    const merchantOrdersSnapshot = await getDocs(merchantOrdersQuery);
    
    let items = [];
    
    if (merchantOrdersSnapshot.docs.length > 0) {
      // Get all merchant order IDs
      const merchantOrderIds = merchantOrdersSnapshot.docs.map(doc => doc.id);
      
      // Query order_items collection by merchantOrderId
      const orderItemsQuery = query(
        orderItemsCollection,
        where('merchantOrderId', 'in', merchantOrderIds),
        where('buyerId', '==', userId)
      );
      
      const itemsSnapshot = await getDocs(orderItemsQuery);
      items = itemsSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
    } else {
      // No merchant orders found for this parent order, return empty array
      // This prevents showing all items on all orders
      items = [];
    }
    
    return items;
  } catch (error) {
    console.error('Error fetching order items for parent order:', error);
    return [];
  }
}

// Optimized function to get orders with items in a single batch
export async function getBuyerOrdersWithItems(userId, limitCount = 10, offset = 0) {
  try {
    const user = auth.currentUser;
    if (!user) throw new Error('Not authenticated');

    const targetUserId = userId || user.uid;
    console.log('🔍 getBuyerOrdersWithItems called for user:', targetUserId, 'limit:', limitCount, 'offset:', offset);
    
    // Get parent orders with pagination
    let parentOrdersQuery = query(
      parentOrdersCollection,
      where('buyerId', '==', targetUserId),
      orderBy('createdAt', 'desc')
    );
    
    // Apply offset and limit for pagination
    if (offset > 0) {
      // For offset-based pagination, we need to get all orders up to offset + limit
      // then slice the results to get the correct batch
      parentOrdersQuery = query(parentOrdersQuery, limit(offset + limitCount));
    } else if (limitCount) {
      parentOrdersQuery = query(parentOrdersQuery, limit(limitCount));
    }
    
    const parentOrdersSnapshot = await getDocs(parentOrdersQuery);
    console.log('📦 Parent orders found:', parentOrdersSnapshot.docs.length);
    
    if (parentOrdersSnapshot.empty) {
      console.log('❌ No parent orders found for user:', targetUserId);
      return [];
    }
    
    const parentOrderIds = parentOrdersSnapshot.docs.map(doc => doc.id);
    
    // Get all merchant orders for these parent orders in a single query
    const merchantOrdersQuery = query(
      merchantOrdersCollection,
      where('parentOrderId', 'in', parentOrderIds),
      where('buyerId', '==', targetUserId)
    );
    
    const merchantOrdersSnapshot = await getDocs(merchantOrdersQuery);
    console.log('🏪 Merchant orders found:', merchantOrdersSnapshot.docs.length);
    
    // Get all merchant order IDs
    const merchantOrderIds = merchantOrdersSnapshot.docs.map(doc => doc.id);
    
    // Get all order items for these merchant orders in a single query
    let orderItems = [];
    if (merchantOrderIds.length > 0) {
      const orderItemsQuery = query(
        orderItemsCollection,
        where('merchantOrderId', 'in', merchantOrderIds),
        where('buyerId', '==', targetUserId)
      );
      
      const orderItemsSnapshot = await getDocs(orderItemsQuery);
      orderItems = orderItemsSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      console.log('📋 Order items found:', orderItems.length);
    }
    
    // Group order items by parent order ID
    const itemsByParentOrder = {};
    orderItems.forEach(item => {
      // Order items don't have parentOrderId directly, we need to get it from merchant orders
      // Let's find the parent order ID by looking up the merchant order
      const merchantOrder = merchantOrdersSnapshot.docs.find(doc => doc.id === item.merchantOrderId);
      const parentOrderId = merchantOrder ? merchantOrder.data().parentOrderId : null;
      
      console.log('🔗 Mapping item to parent order:', parentOrderId, 'Item merchantOrderId:', item.merchantOrderId, 'Item:', item);
      
      if (parentOrderId) {
        if (!itemsByParentOrder[parentOrderId]) {
          itemsByParentOrder[parentOrderId] = [];
        }
        itemsByParentOrder[parentOrderId].push(item);
      }
    });
    
    console.log('📊 Items by parent order mapping:', itemsByParentOrder);
    
    // Get unique seller IDs from order items to fetch shop names
    const sellerIds = [...new Set(orderItems.map(item => item.sellerId).filter(Boolean))];
    console.log('🔍 Seller IDs to fetch shop names for:', sellerIds);
    console.log('🔍 First seller ID length:', sellerIds[0]?.length);
    console.log('🔍 First seller ID:', sellerIds[0]);
    const shopNamesMap = await getShopNamesBySellerIds(sellerIds);
    console.log('🏪 Shop names map result:', shopNamesMap);
    
    // Combine parent orders with their items
    const allOrdersWithItems = parentOrdersSnapshot.docs.map(doc => {
      const orderData = doc.data();
      const parentOrderId = doc.id;
      console.log('🔍 Document ID:', parentOrderId, 'Order data:', orderData);
      
      // Enhance order items with shop names
      const enhancedOrderItems = (itemsByParentOrder[parentOrderId] || []).map(item => ({
        ...item,
        shopName: shopNamesMap[item.sellerId] || `Shop ${item.sellerId?.slice(-4)}`
      }));
      
      return {
        ...orderData,
        id: parentOrderId, // Set id AFTER spreading orderData to ensure it's not overridden
        orderItems: enhancedOrderItems
      };
    });
    
    // Apply offset slicing for pagination
    const ordersWithItems = offset > 0 ? allOrdersWithItems.slice(offset) : allOrdersWithItems;
    
    console.log('✅ Final orders with items:', ordersWithItems.length, '(total fetched:', allOrdersWithItems.length, ', offset:', offset, ')');
    return ordersWithItems;
  } catch (error) {
    console.error('❌ Error getting buyer orders with items:', error);
    throw error;
  }
}

// Subscribe to real-time updates for a parent order
export function subscribeToParentOrder(parentOrderId, callback) {
  try {
    const parentOrderRef = doc(db, 'parent_orders', parentOrderId);
    
    return onSnapshot(parentOrderRef, async (doc) => {
      if (doc.exists()) {
        const parentOrder = { id: doc.id, ...doc.data() };
        
        // Get updated merchant orders and items
        const details = await getParentOrderWithDetails(parentOrderId);
        callback(details);
      } else {
        callback(null);
      }
    });
  } catch (error) {
    console.error('Error subscribing to parent order:', error);
    return null;
  }
}

// Optimized function to get seller orders with items in a single batch
export async function getSellerOrdersWithItems(sellerId, limitCount = 10) {
  try {
    const user = auth.currentUser;
    if (!user) throw new Error('Not authenticated');

    const targetSellerId = sellerId || user.uid;
    console.log('🔍 getSellerOrdersWithItems called for seller:', targetSellerId, 'limit:', limitCount);

    // Get merchant orders for this seller
    let merchantOrdersQuery = query(
      merchantOrdersCollection,
      where('sellerId', '==', targetSellerId),
      orderBy('createdAt', 'desc')
    );

    if (limitCount) {
      merchantOrdersQuery = query(merchantOrdersQuery, limit(limitCount));
    }

    const merchantOrdersSnapshot = await getDocs(merchantOrdersQuery);
    console.log('🏪 Merchant orders found:', merchantOrdersSnapshot.docs.length);

    if (merchantOrdersSnapshot.empty) {
      console.log('❌ No merchant orders found for seller:', targetSellerId);
      return [];
    }

    const merchantOrderIds = merchantOrdersSnapshot.docs.map(doc => doc.id);

    // Get order items for these merchant orders
    let orderItems = [];
    if (merchantOrderIds.length > 0) {
      const orderItemsQuery = query(
        orderItemsCollection,
        where('merchantOrderId', 'in', merchantOrderIds),
        where('sellerId', '==', targetSellerId)
      );

      const orderItemsSnapshot = await getDocs(orderItemsQuery);
      orderItems = orderItemsSnapshot.docs.map(doc => {
        const itemData = doc.data();
        // Log each item's status for debugging
        console.log('📦 Item:', doc.id, 'MerchantOrderId:', itemData.merchantOrderId, 'Status:', itemData.status || 'MISSING', 'Has status field:', 'status' in itemData);
        return {
          id: doc.id,
          ...itemData,
          status: itemData.status || null // Ensure status field exists even if null
        };
      });
      console.log('📋 Order items found:', orderItems.length);
      // Log status summary
      const statusSummary = orderItems.reduce((acc, item) => {
        const status = item.status || 'NULL';
        acc[status] = (acc[status] || 0) + 1;
        return acc;
      }, {});
      console.log('📊 Item status summary:', statusSummary);
    }

    // Group order items by merchant order ID
    const itemsByMerchantOrder = {};
    orderItems.forEach(item => {
      const merchantOrderId = item.merchantOrderId;
      console.log('🔗 Mapping item to merchant order:', merchantOrderId, 'Item merchantOrderId:', item.merchantOrderId, 'Item:', item);
      
      if (merchantOrderId) {
        if (!itemsByMerchantOrder[merchantOrderId]) {
          itemsByMerchantOrder[merchantOrderId] = [];
        }
        itemsByMerchantOrder[merchantOrderId].push(item);
      }
    });
    
    console.log('📊 Items by merchant order mapping:', itemsByMerchantOrder);

    // Combine merchant orders with their items
    const ordersWithItems = merchantOrdersSnapshot.docs.map(doc => {
      const orderData = doc.data();
      const merchantOrderId = doc.id;
      // Get status from orderData, with fallback to 'PLACED'
      const orderStatus = (orderData && orderData.status) ? orderData.status : 'PLACED';
      console.log('🔍 Document ID:', merchantOrderId, 'Raw orderData.status:', orderData?.status, 'Resolved orderStatus:', orderStatus, 'Order data keys:', Object.keys(orderData || {}));
      
      // Build order object, ensuring status is explicitly set
      const orderWithItems = {
        ...orderData,
        id: merchantOrderId,
        status: orderStatus, // Explicitly set status (this overrides any undefined status from orderData)
        orderItems: itemsByMerchantOrder[merchantOrderId] || []
      };
      
      console.log('📦 Final order object status:', orderWithItems.status, 'Has status?', 'status' in orderWithItems);
      
      return orderWithItems;
    });

    console.log('✅ Final seller orders with items:', ordersWithItems.length);
    return ordersWithItems;
  } catch (error) {
    console.error('❌ Error getting seller orders with items:', error);
    throw error;
  }
}

// Update group status - applies to all items in the group
export async function updateGroupStatus(groupId, newStatus, sellerId = null) {
  try {
    const user = auth.currentUser;
    if (!user) throw new Error('Not authenticated');

    const targetSellerId = sellerId || user.uid;
    
    // Parse group ID to extract components
    const { parentOrderId, sellerId: groupSellerId, serviceDate } = parseGroupId(groupId);
    console.log('🔍 Parsed group ID:', { groupId, parentOrderId, groupSellerId, serviceDate });
    
    // Verify seller has permission to update this group
    if (groupSellerId !== targetSellerId) {
      throw new Error('Unauthorized: Cannot update other seller\'s orders');
    }

    console.log('🔄 Updating group status:', { groupId, newStatus, parentOrderId, groupSellerId, serviceDate });

    // First, get the merchant order for this seller and parent order
    const merchantOrderQuery = query(
      merchantOrdersCollection,
      where('parentOrderId', '==', parentOrderId),
      where('sellerId', '==', groupSellerId)
    );

    const merchantOrderSnapshot = await getDocs(merchantOrderQuery);
    
    if (merchantOrderSnapshot.empty) {
      throw new Error('No merchant order found for this group');
    }

    const merchantOrderId = merchantOrderSnapshot.docs[0].id;
    const merchantOrderData = merchantOrderSnapshot.docs[0].data();
    
    // Verify the merchant order belongs to this seller
    if (merchantOrderData.sellerId !== targetSellerId) {
      console.error('❌ Merchant order seller mismatch:', {
        merchantOrderId,
        merchantOrderSellerId: merchantOrderData.sellerId,
        expectedSellerId: targetSellerId,
        currentUserId: user.uid
      });
      throw new Error('Merchant order does not belong to this seller. Permission denied.');
    }
    
    console.log('✅ Verified merchant order belongs to seller:', {
      merchantOrderId,
      sellerId: merchantOrderData.sellerId,
      status: merchantOrderData.status
    });

    // Get all order items for this group
    const orderItemsQuery = query(
      orderItemsCollection,
      where('merchantOrderId', '==', merchantOrderId),
      where('sellerId', '==', groupSellerId),
      where('serviceDate', '==', serviceDate)
    );

    const itemsSnapshot = await getDocs(orderItemsQuery);
    
    if (itemsSnapshot.empty) {
      throw new Error('No items found for this group');
    }

    // Verify all items belong to this seller before updating
    const itemDocs = itemsSnapshot.docs.filter(doc => {
      const itemData = doc.data();
      const itemSellerId = itemData.sellerId;
      if (itemSellerId !== targetSellerId) {
        console.warn('⚠️ Order item has mismatched sellerId:', {
          itemId: doc.id,
          itemSellerId,
          expectedSellerId: targetSellerId,
          merchantOrderId
        });
        return false;
      }
      return true;
    });

    if (itemDocs.length === 0) {
      throw new Error('No valid items found for this seller. All items have mismatched sellerId.');
    }

    console.log(`✅ Verified ${itemDocs.length} items belong to seller ${targetSellerId}`);
    
    // Log current item statuses before update
    const currentItemStatuses = itemDocs.map(doc => {
      const data = doc.data();
      return { 
        id: doc.id, 
        status: data.status || 'NULL',
        hasStatusField: 'status' in data
      };
    });
    console.log('📋 Current item statuses before update:', currentItemStatuses);

    // Update all items in the group - ALWAYS update items even if merchant order status doesn't change
    // This ensures items have the status field, even if they were missing it before
    const batch = writeBatch(db);
    const itemIds = [];
    
    // Normalize status to uppercase for consistency
    const normalizedItemStatus = newStatus?.toUpperCase() || newStatus;
    
    itemDocs.forEach(doc => {
      const itemRef = doc.ref;
      const currentData = doc.data();
      const currentItemStatus = (currentData.status || '').toUpperCase();
      const itemStatusNeedsUpdate = !currentData.status || currentItemStatus !== normalizedItemStatus;
      
      console.log(`🔄 Item ${doc.id}: current="${currentItemStatus}", target="${normalizedItemStatus}", needs update=${itemStatusNeedsUpdate}`);
      
      // Only update if status actually needs to change to prevent infinite loops
      // ALSO skip if refund has already been processed (refundStatus exists) - don't update canceled items with refund info
      const hasRefundStatus = currentData.refundStatus !== undefined;
      if (itemStatusNeedsUpdate && !hasRefundStatus) {
        batch.update(itemRef, {
          status: normalizedItemStatus,
          updatedAt: serverTimestamp()
        });
        itemIds.push(doc.id);
      } else {
        if (hasRefundStatus) {
          console.log(`⏭️ Skipping item ${doc.id} - refund already processed (status: ${currentData.refundStatus}), no update needed`);
        } else {
          console.log(`⏭️ Skipping item ${doc.id} - status already "${currentItemStatus}", no update needed`);
        }
      }
    });
    
    // Only commit if there are items to update
    if (itemIds.length > 0) {
      await batch.commit();
      console.log(`✅ Updated ${itemIds.length} order items to status: ${normalizedItemStatus}`);
    } else {
      console.log('⏭️ No items need status update, skipping batch commit to prevent infinite loop');
    }
    
    // Verify the update by reading back the items immediately after commit (only if we updated items)
    if (itemIds.length > 0) {
      // Wait a moment for Firestore to propagate the update
      await new Promise(resolve => setTimeout(resolve, 200));
      
      const verifySnapshot = await getDocs(orderItemsQuery);
      const updatedStatuses = verifySnapshot.docs.map(doc => {
        const data = doc.data();
        return { 
          id: doc.id, 
          status: data.status || 'NULL',
          hasStatusField: 'status' in data,
          allKeys: Object.keys(data)
        };
      });
      console.log('✅ Verified item statuses after update:', updatedStatuses);
      
      // Check if any items are still missing status
      const missingStatus = updatedStatuses.filter(item => !item.hasStatusField || !item.status || item.status === 'NULL');
      if (missingStatus.length > 0) {
        console.error('❌ Items still missing status after update:', missingStatus);
      }
    }
    
    // Update the merchant order status to trigger email alerts
    // Normalize status to uppercase to ensure Cloud Function recognizes it
    const normalizedStatus = newStatus?.toUpperCase() || newStatus;
    const merchantOrderRef = doc(db, 'merchant_orders', merchantOrderId);
    
    // Get current status before update for logging
    const currentMerchantOrderSnap = await getDoc(merchantOrderRef);
    const currentStatusData = currentMerchantOrderSnap.data();
    const currentStatus = (currentStatusData?.status || '').toUpperCase();
    const statusWillChange = currentStatus !== normalizedStatus;
    
    console.log('📧 [updateGroupStatus] Status update details:', {
      merchantOrderId,
      currentStatus: currentStatusData?.status,
      currentStatusUpper: currentStatus,
      newStatus: newStatus,
      normalizedStatus: normalizedStatus,
      statusWillChange: statusWillChange
    });
    
    // Only update merchant order if status actually needs to change to prevent infinite loops
    if (statusWillChange) {
      await updateDoc(merchantOrderRef, {
        status: normalizedStatus,
        updatedAt: serverTimestamp()
      });
      console.log('✅ Merchant order status updated from', currentStatusData?.status, 'to', normalizedStatus);
    } else {
      console.log('⏭️ Merchant order status already', normalizedStatus, '- skipping update to prevent infinite loop');
    }
    
    console.log('✅ Group status updated successfully:', { groupId, newStatus, normalizedStatus, itemCount: itemIds.length });
    console.log('📧 Merchant order updated to trigger email alert:', { merchantOrderId, currentStatus, newStatus, normalizedStatus });
    
    // If merchant order is being canceled, check if all merchant orders for the parent order are canceled
    // If so, update the parent order to canceled as well
    if (normalizedStatus === 'CANCELED' || normalizedStatus === 'CANCELLED') {
      console.log('🔄 Checking if all merchant orders for parent order are canceled...');
      
      try {
        // Get all merchant orders for this parent order
        const allMerchantOrdersQuery = query(
          merchantOrdersCollection,
          where('parentOrderId', '==', parentOrderId)
        );
        
        const allMerchantOrdersSnapshot = await getDocs(allMerchantOrdersQuery);
        console.log(`📋 Found ${allMerchantOrdersSnapshot.docs.length} merchant orders for parent order ${parentOrderId}`);
        
        // Check if all merchant orders are canceled
        const allCanceled = allMerchantOrdersSnapshot.docs.every(doc => {
          const status = (doc.data().status || '').toUpperCase();
          return status === 'CANCELED' || status === 'CANCELLED';
        });
        
        console.log(`🔍 All merchant orders canceled? ${allCanceled}`);
        
        if (allCanceled) {
          // Update parent order to canceled
          const parentOrderRef = doc(db, 'parent_orders', parentOrderId);
          const parentOrderSnap = await getDoc(parentOrderRef);
          
          if (parentOrderSnap.exists()) {
            const parentOrderData = parentOrderSnap.data();
            const parentOrderStatus = (parentOrderData?.status || '').toUpperCase();
            
            // Only update if parent order is not already canceled
            if (parentOrderStatus !== 'CANCELED' && parentOrderStatus !== 'CANCELLED') {
              await updateDoc(parentOrderRef, {
                status: 'CANCELLED',
                updatedAt: serverTimestamp()
              });
              console.log(`✅ Parent order ${parentOrderId} updated to CANCELLED (all merchant orders canceled)`);
            } else {
              console.log(`⏭️ Parent order ${parentOrderId} already canceled, skipping update`);
            }
          } else {
            console.log(`⚠️ Parent order ${parentOrderId} not found, skipping parent order update`);
          }
        }
      } catch (error) {
        console.error('❌ Error checking/updating parent order status:', error);
        // Don't throw - this is a secondary update, shouldn't fail the main operation
      }
    }
    
    // IMPORTANT: The Cloud Function will trigger on this Firestore document update
    // It watches for changes to merchant_orders/{merchantOrderId} and sends email if status changed
    console.log('📧 Cloud Function trigger: Firestore update should trigger onMerchantOrderStatusChange');
    console.log('📧 Expected behavior: Status changed from', currentStatus, 'to', normalizedStatus);
    
    return {
      success: true,
      groupId,
      newStatus,
      updatedItems: itemIds.length,
      merchantOrderId
    };
  } catch (error) {
    console.error('❌ Error updating group status:', error);
    throw error;
  }
}

// Get group status aggregation
export function getGroupStatus(items, merchantOrderStatus = null) {
  if (!items || items.length === 0) {
    // If no items, check merchant order status
    if (merchantOrderStatus) {
      const normalized = (merchantOrderStatus || '').toUpperCase();
      if (normalized === 'CANCELLED' || normalized === 'CANCELED') {
        return 'CANCELLED';
      }
      return normalized || 'PLACED';
    }
    return 'PLACED';
  }
  
  // Get statuses from items, including null/undefined values for debugging
  const statuses = items.map(item => item?.status);
  const statusesWithValues = statuses.filter(s => s != null && s !== ''); // Filter out null, undefined, empty string
  
  console.log('🔍 [getGroupStatus] Input:', {
    itemsCount: items.length,
    itemStatuses: statuses,
    statusesWithValues: statusesWithValues,
    merchantOrderStatus: merchantOrderStatus,
    merchantOrderStatusType: typeof merchantOrderStatus
  });
  
  // If no item statuses at all, use merchant order status
  if (statusesWithValues.length === 0) {
    console.log('⚠️ [getGroupStatus] No item statuses found. Items:', items.map(item => ({ 
      id: item?.id?.substring(0, 8) || 'no-id', 
      hasStatus: 'status' in (item || {}), 
      status: item?.status || 'NULL',
      itemKeys: Object.keys(item || {})
    })));
    
    if (merchantOrderStatus) {
      const normalized = (merchantOrderStatus || '').toUpperCase();
      console.log('✅ [getGroupStatus] No item statuses, using merchant order status:', normalized);
      if (normalized === 'CANCELLED' || normalized === 'CANCELED') {
        console.log('✅ [getGroupStatus] Returning CANCELLED from merchant order status');
        return 'CANCELLED';
      }
      const result = normalized || 'PLACED';
      console.log('✅ [getGroupStatus] Returning merchant order status:', result);
      return result;
    }
    console.log('⚠️ [getGroupStatus] No item statuses and no merchant order status, defaulting to PLACED');
    return 'PLACED';
  }
  
  // Normalize statuses to uppercase for comparison
  const normalizedStatuses = statusesWithValues.map(s => (s || '').toUpperCase());
  
  // If all items are cancelled, group is cancelled
  if (normalizedStatuses.every(status => status === 'CANCELLED' || status === 'CANCELED')) {
    console.log('✅ [getGroupStatus] All items are cancelled, returning CANCELLED');
    return 'CANCELLED';
  }
  
  // Remove cancelled items from consideration
  const activeStatuses = statusesWithValues.filter(status => {
    const normalized = (status || '').toUpperCase();
    return normalized !== 'CANCELLED' && normalized !== 'CANCELED';
  });
  
  console.log('🔍 [getGroupStatus] Active statuses (non-cancelled):', activeStatuses);
  
  if (activeStatuses.length === 0) {
    // All items cancelled - check merchant order as fallback
    console.log('🔍 [getGroupStatus] No active statuses, checking merchant order:', merchantOrderStatus);
    if (merchantOrderStatus) {
      const normalized = (merchantOrderStatus || '').toUpperCase();
      console.log('🔍 [getGroupStatus] Normalized merchant order status:', normalized);
      if (normalized === 'CANCELLED' || normalized === 'CANCELED') {
        console.log('✅ [getGroupStatus] Merchant order is cancelled, returning CANCELLED');
        return 'CANCELLED';
      }
      console.log('✅ [getGroupStatus] Returning merchant order status:', normalized || 'PLACED');
      return normalized || 'PLACED';
    }
    console.log('✅ [getGroupStatus] No merchant order status, returning CANCELLED');
    return 'CANCELLED'; // All items cancelled
  }
  
  // Status priority: completed > ready > in-progress > accepted > placed > cancelled
  const statusPriority = {
    'completed': 6,
    'COMPLETED': 6,
    'ready': 5,
    'READY': 5,
    'in-progress': 4,
    'IN-PROGRESS': 4,
    'accepted': 3,
    'ACCEPTED': 3,
    'placed': 2,
    'PLACED': 2,
    'cancelled': 0,
    'CANCELLED': 0,
    'canceled': 0,
    'CANCELED': 0
  };
  
  // Return the highest priority status
  const highestPriority = Math.max(...activeStatuses.map(status => statusPriority[status] || 0));
  const resultStatus = activeStatuses.find(status => statusPriority[status] === highestPriority);
  
  return resultStatus?.toUpperCase() || 'PLACED';
}

// Generate group ID
export function generateGroupId(parentOrderId, sellerId, serviceDate) {
  return `${parentOrderId}-${sellerId}-${serviceDate}`;
}

// Parse group ID to extract components
export function parseGroupId(groupId) {
  // Group ID format: parentOrderId-sellerId-serviceDate
  // serviceDate can contain hyphens (e.g., "2024-01-15"), so we need to handle this carefully
  const groupParts = groupId.split('-');
  if (groupParts.length < 3) {
    throw new Error(`Invalid group ID format: ${groupId}. Expected format: parentOrderId-sellerId-serviceDate`);
  }
  
  const parentOrderId = groupParts[0];
  const groupSellerId = groupParts[1];
  const serviceDate = groupParts.slice(2).join('-'); // Handle dates with hyphens
  
  return {
    parentOrderId,
    sellerId: groupSellerId,
    serviceDate
  };
}

// Subscribe to real-time updates for seller merchant orders
export function subscribeToSellerMerchantOrders(sellerId = null, callback) {
  try {
    const user = auth.currentUser;
    if (!user) return null;

    const targetSellerId = sellerId || user.uid;

    const merchantOrdersQuery = query(
      merchantOrdersCollection,
      where('sellerId', '==', targetSellerId),
      orderBy('createdAt', 'desc')
    );
    
    return onSnapshot(merchantOrdersQuery, async (snapshot) => {
      const merchantOrders = [];
      
      for (const doc of snapshot.docs) {
        const merchantOrder = { id: doc.id, ...doc.data() };
        
        // Get items for this merchant order
        const orderItemsQuery = query(
          orderItemsCollection,
          where('merchantOrderId', '==', doc.id),
          orderBy('serviceDate', 'asc')
        );
        
        const itemsSnapshot = await getDocs(orderItemsQuery);
        merchantOrder.items = itemsSnapshot.docs.map(itemDoc => ({
          id: itemDoc.id,
          ...itemDoc.data()
        }));
        
        merchantOrders.push(merchantOrder);
      }
      
      callback(merchantOrders);
    });
  } catch (error) {
    console.error('Error subscribing to seller merchant orders:', error);
    return null;
  }
}

// Cancel specific order items
export async function cancelOrderItems(itemIds, reason = '') {
  try {
    const user = auth.currentUser;
    if (!user) throw new Error('Not authenticated');

    const batch = writeBatch(db);
    const merchantOrderIds = new Set();

    for (const itemId of itemIds) {
      const itemRef = doc(db, 'order_items', itemId);
      const itemSnap = await getDoc(itemRef);
      
      if (!itemSnap.exists()) {
        throw new Error(`Order item ${itemId} not found`);
      }

      const itemData = itemSnap.data();
      merchantOrderIds.add(itemData.merchantOrderId);

      // Verify user is the seller
      const merchantOrderRef = doc(db, 'merchant_orders', itemData.merchantOrderId);
      const merchantOrderSnap = await getDoc(merchantOrderRef);
      
      if (!merchantOrderSnap.exists()) {
        throw new Error('Merchant order not found');
      }

      const merchantOrderData = merchantOrderSnap.data();
      if (merchantOrderData.sellerId !== user.uid) {
        throw new Error('Not authorized to cancel this order');
      }

      // Update item status to cancelled
      batch.update(itemRef, {
        status: 'cancelled',
        cancellationReason: reason,
        updatedAt: serverTimestamp()
      });
    }

    await batch.commit();

    // Update merchant order statuses
    for (const merchantOrderId of merchantOrderIds) {
      await updateMerchantOrderStatus(merchantOrderId);
    }

    return { success: true, cancelledItems: itemIds.length };
  } catch (error) {
    console.error('Error cancelling order items:', error);
    throw error;
  }
}

// Get order items grouped by service date for a merchant order
export function groupOrderItemsByServiceDate(items) {
  const grouped = {};
  
  items.forEach(item => {
    const serviceDate = item.serviceDate;
    if (!grouped[serviceDate]) {
      grouped[serviceDate] = [];
    }
    grouped[serviceDate].push(item);
  });

  // Sort dates and items within each date
  const sortedDates = Object.keys(grouped).sort();
  const result = {};
  
  sortedDates.forEach(date => {
    result[date] = grouped[date].sort((a, b) => {
      // Sort by window start time, then by product name
      const timeA = a.windowStartLocal || '00:00';
      const timeB = b.windowStartLocal || '00:00';
      if (timeA !== timeB) {
        return timeA.localeCompare(timeB);
      }
      return (a.productSnapshot?.name || '').localeCompare(b.productSnapshot?.name || '');
    });
  });

  return result;
}

// Calculate merchant order totals
export function calculateMerchantOrderTotals(items) {
  const totals = {
    subtotal: 0,
    tax: 0,
    total: 0
  };

  items.forEach(item => {
    if (item.status !== 'cancelled') {
      const itemTotal = item.unitPrice * item.quantity;
      totals.subtotal += itemTotal;
    }
  });

  totals.tax = Math.round(totals.subtotal * 0.08 * 100) / 100; // 8% tax
  totals.total = totals.subtotal + totals.tax;

  return totals;
}

// PRD: Order State Management Functions

// Update order state with proper tracking
export async function updateOrderState(parentOrderId, newState, triggeredBy, notes = '') {
  try {
    const user = auth.currentUser;
    if (!user) throw new Error('Not authenticated');

    const parentOrderRef = doc(db, 'parent_orders', parentOrderId);
    const parentOrderSnap = await getDoc(parentOrderRef);
    
    if (!parentOrderSnap.exists()) {
      throw new Error('Parent order not found');
    }

    const parentOrderData = parentOrderSnap.data();
    const currentState = parentOrderData.orderStates?.current || 'pending';
    
    // Validate state transition
    const validTransitions = {
      'pending': ['accepted', 'canceled'],
      'accepted': ['in_progress', 'canceled'],
      'in_progress': ['completed', 'canceled'],
      'completed': [], // Terminal state
      'canceled': []   // Terminal state
    };

    if (!validTransitions[currentState]?.includes(newState)) {
      throw new Error(`Invalid state transition from ${currentState} to ${newState}`);
    }

    // Add new state to history
    const newStateEntry = {
      state: newState,
      timestamp: serverTimestamp(),
      triggeredBy: triggeredBy,
      notes: notes
    };

    const updatedOrderStates = {
      current: newState,
      history: [...(parentOrderData.orderStates?.history || []), newStateEntry]
    };

    // Update the order
    await updateDoc(parentOrderRef, {
      status: newState,
      orderStates: updatedOrderStates,
      updatedAt: serverTimestamp()
    });

    console.log(`Order state updated: ${parentOrderId} ${currentState} → ${newState} by ${triggeredBy}`);
    return { success: true, newState, previousState: currentState };

  } catch (error) {
    console.error('Error updating order state:', error);
    throw error;
  }
}

// Seller accepts order (triggers payment capture)
export async function acceptOrder(parentOrderId, sellerId) {
  try {
    // Verify seller has permission
    const parentOrderRef = doc(db, 'parent_orders', parentOrderId);
    const parentOrderSnap = await getDoc(parentOrderRef);
    
    if (!parentOrderSnap.exists()) {
      throw new Error('Parent order not found');
    }

    const parentOrderData = parentOrderSnap.data();
    
    // Check if seller is associated with this order
    const merchantOrdersQuery = query(
      merchantOrdersCollection,
      where('parentOrderId', '==', parentOrderId),
      where('sellerId', '==', sellerId)
    );
    const merchantOrdersSnap = await getDocs(merchantOrdersQuery);
    
    if (merchantOrdersSnap.empty) {
      throw new Error('Seller not authorized to accept this order');
    }

    // Update order state to accepted
    await updateOrderState(parentOrderId, 'accepted', 'seller', 'Seller accepted the order');

    // Update merchant orders to accepted (uppercase for email trigger)
    const batch = writeBatch(db);
    merchantOrdersSnap.docs.forEach(doc => {
      batch.update(doc.ref, {
        status: 'ACCEPTED',
        acceptedAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      });
    });

    // Update order items to accepted
    const orderItemsQuery = query(
      orderItemsCollection,
      where('parentOrderId', '==', parentOrderId)
    );
    const orderItemsSnap = await getDocs(orderItemsQuery);
    
    orderItemsSnap.docs.forEach(doc => {
      batch.update(doc.ref, {
        status: 'accepted',
        acceptedAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      });
    });

    await batch.commit();

    console.log(`Order accepted: ${parentOrderId} by seller ${sellerId}`);
    return { success: true };

  } catch (error) {
    console.error('Error accepting order:', error);
    throw error;
  }
}

// Update order breakdown with actual Stripe fees after payment completion
export async function updateOrderBreakdownWithStripeFees(parentOrderId, paymentIntentId) {
  try {
    console.log('Updating order breakdown with Stripe fees:', { parentOrderId, paymentIntentId });

    // Get the parent order
    const parentOrderRef = doc(db, 'parent_orders', parentOrderId);
    const parentOrderSnap = await getDoc(parentOrderRef);
    
    if (!parentOrderSnap.exists()) {
      throw new Error('Parent order not found');
    }

    const parentOrderData = parentOrderSnap.data();
    const breakdown = parentOrderData.breakdown || {};

    // Calculate Stripe fees (2.9% + $0.30)
    const grossAmount = breakdown.grossOrderAmount || 0;
    const stripeProcessingFee = Math.round((grossAmount * 0.029 + 0.30) * 100) / 100; // 2.9% + $0.30
    
    // Calculate net seller payout
    const netSellerPayout = grossAmount - stripeProcessingFee - breakdown.vestaFee;

    // Update the breakdown
    const updatedBreakdown = {
      ...breakdown,
      stripeFees: stripeProcessingFee,
      netSellerPayout: Math.max(0, netSellerPayout), // Ensure non-negative
      updatedAt: serverTimestamp()
    };

    // Update the parent order
    await updateDoc(parentOrderRef, {
      breakdown: updatedBreakdown,
      updatedAt: serverTimestamp()
    });

    console.log('Order breakdown updated:', {
      parentOrderId,
      grossAmount,
      stripeFees: stripeProcessingFee,
      vestaFee: breakdown.vestaFee,
      netSellerPayout: netSellerPayout
    });

    return updatedBreakdown;

  } catch (error) {
    console.error('Error updating order breakdown:', error);
    throw error;
  }
}

// PRD: State-based cancellation with proper fee handling
export async function cancelOrderWithStateHandling(parentOrderId, reason = '', initiatedBy = 'buyer') {
  try {
    const user = auth.currentUser;
    if (!user) throw new Error('Not authenticated');

    console.log('Canceling order with state handling:', { parentOrderId, reason, initiatedBy });

    // Get the parent order
    const parentOrderRef = doc(db, 'parent_orders', parentOrderId);
    const parentOrderSnap = await getDoc(parentOrderRef);
    
    if (!parentOrderSnap.exists()) {
      throw new Error('Parent order not found');
    }

    const parentOrderData = parentOrderSnap.data();
    const currentState = parentOrderData.orderStates?.current || 'pending';
    const breakdown = parentOrderData.breakdown || {};
    
    console.log('Current order state:', currentState);

    // PRD: Different cancellation rules based on state
    let refundBreakdown = {
      buyerRefund: 0,
      stripeFeesLost: 0,
      vestaFeeRefunded: 0,
      sellerAbsorbsFees: false
    };

    if (currentState === 'pending') {
      // PRD: Cancel while Pending - Full refund, no fees applied
      refundBreakdown = {
        buyerRefund: breakdown.grossOrderAmount || 0,
        stripeFeesLost: 0,
        vestaFeeRefunded: breakdown.vestaFee || 0,
        sellerAbsorbsFees: false
      };
    } else if (['accepted', 'in_progress'].includes(currentState)) {
      // PRD: Cancel after acceptance - Seller absorbs Stripe fees, Vesta refunds $2
      refundBreakdown = {
        buyerRefund: breakdown.grossOrderAmount || 0,
        stripeFeesLost: breakdown.stripeFees || 0,
        vestaFeeRefunded: breakdown.vestaFee || 0,
        sellerAbsorbsFees: true
      };
    } else {
      throw new Error(`Cannot cancel order in ${currentState} state`);
    }

    // Update order state to canceled
    await updateOrderState(parentOrderId, 'canceled', initiatedBy, reason);

    // Update merchant orders and items
    const merchantOrdersQuery = query(
      merchantOrdersCollection,
      where('parentOrderId', '==', parentOrderId)
    );
    const merchantOrdersSnap = await getDocs(merchantOrdersQuery);

    const batch = writeBatch(db);
    merchantOrdersSnap.docs.forEach(doc => {
      batch.update(doc.ref, {
        status: 'canceled',
        cancellationReason: reason,
        cancelledAt: serverTimestamp(),
        cancelledBy: initiatedBy,
        refundBreakdown: refundBreakdown,
        updatedAt: serverTimestamp()
      });
    });

    // Update order items
    const orderItemsQuery = query(
      orderItemsCollection,
      where('parentOrderId', '==', parentOrderId)
    );
    const orderItemsSnap = await getDocs(orderItemsQuery);
    
    orderItemsSnap.docs.forEach(doc => {
      batch.update(doc.ref, {
        status: 'canceled',
        cancellationReason: reason,
        cancelledAt: serverTimestamp(),
        cancelledBy: initiatedBy,
        updatedAt: serverTimestamp()
      });
    });

    await batch.commit();

    console.log('Order cancellation completed with refund breakdown:', refundBreakdown);
    return { 
      success: true, 
      orderId: parentOrderId,
      refundBreakdown: refundBreakdown,
      previousState: currentState
    };

  } catch (error) {
    console.error('Error canceling order with state handling:', error);
    throw error;
  }
}

// Cancel a customer order (parent order and all merchant orders) - Legacy function
export async function cancelCustomerOrder(parentOrderId, reason = '') {
  try {
    const user = auth.currentUser;
    if (!user) throw new Error('Not authenticated');

    console.log(`🔄 Customer cancelling order: ${parentOrderId}`);

    // Get the parent order to verify ownership
    const parentOrderRef = doc(db, 'parent_orders', parentOrderId);
    const parentOrderSnap = await getDoc(parentOrderRef);
    
    if (!parentOrderSnap.exists()) {
      throw new Error('Order not found');
    }

    const parentOrderData = parentOrderSnap.data();
    
    // Verify the user owns this order
    if (parentOrderData.userId !== user.uid) {
      throw new Error('Not authorized to cancel this order');
    }

    // Check if order can be cancelled (only PLACED orders can be cancelled by customer)
    console.log('🔍 Parent order status check (full order):', { 
      status: parentOrderData.status, 
      expected: 'PLACED',
      canCancel: parentOrderData.status === 'PLACED' || parentOrderData.status === 'placed'
    });
    
    if (parentOrderData.status !== 'PLACED' && parentOrderData.status !== 'placed') {
      throw new Error('This order cannot be cancelled. Only orders with PLACED status can be cancelled.');
    }

    // Get all merchant orders for this parent order
    const merchantOrdersQuery = query(
      merchantOrdersCollection,
      where('parentOrderId', '==', parentOrderId)
    );
    
    const merchantOrdersSnapshot = await getDocs(merchantOrdersQuery);
    
    if (merchantOrdersSnapshot.empty) {
      throw new Error('No merchant orders found for this order');
    }

    // Update parent order status
    const batch = writeBatch(db);
    
    // Update parent order
    batch.update(parentOrderRef, {
      status: 'CANCELLED',
      cancellationReason: reason,
      cancelledAt: serverTimestamp(),
      cancelledBy: 'customer',
      updatedAt: serverTimestamp()
    });

    // Update all merchant orders
    const merchantOrderIds = [];
    merchantOrdersSnapshot.docs.forEach(doc => {
      const merchantOrderId = doc.id;
      merchantOrderIds.push(merchantOrderId);
      
      batch.update(doc.ref, {
        status: 'canceled',
        cancellationReason: reason,
        cancelledAt: serverTimestamp(),
        cancelledBy: 'customer',
        updatedAt: serverTimestamp(),
        processing: true  // Prevent duplicate Cloud Function processing
      });
    });

    // Get all order items for these merchant orders and mark them as cancelled
    const orderItemsPromises = merchantOrderIds.map(async (merchantOrderId) => {
      const orderItemsQuery = query(
        orderItemsCollection,
        where('merchantOrderId', '==', merchantOrderId)
      );
      
      const orderItemsSnapshot = await getDocs(orderItemsQuery);
      
      orderItemsSnapshot.docs.forEach(itemDoc => {
        batch.update(itemDoc.ref, {
          status: 'cancelled',
          cancelled: true,
          cancellationReason: reason,
          cancelledAt: serverTimestamp(),
          cancelledBy: 'customer',
          updatedAt: serverTimestamp()
        });
      });
    });

    // Wait for all order items to be processed
    await Promise.all(orderItemsPromises);

    // Commit all updates
    await batch.commit();

    console.log(`✅ Customer order ${parentOrderId} cancelled successfully`);
    
    return { 
      success: true, 
      message: 'Order cancelled successfully',
      cancelledMerchantOrders: merchantOrderIds.length
    };

  } catch (error) {
    console.error('❌ Error cancelling customer order:', error);
    throw error;
  }
}

// Cancel a specific merchant order group (for partial cancellations)
export async function cancelCustomerOrderGroup(groupId, reason = '') {
  try {
    const user = auth.currentUser;
    if (!user) throw new Error('Not authenticated');

    console.log(`🔄 Customer cancelling order group: ${groupId}`);

    // Parse group ID to extract components
    const { parentOrderId, sellerId: groupSellerId, serviceDate } = parseGroupId(groupId);
    console.log('🔍 Parsed group ID:', { groupId, parentOrderId, groupSellerId, serviceDate });

    // Get the parent order to verify ownership
    const parentOrderRef = doc(db, 'parent_orders', parentOrderId);
    const parentOrderSnap = await getDoc(parentOrderRef);
    
    if (!parentOrderSnap.exists()) {
      throw new Error('Order not found');
    }

    const parentOrderData = parentOrderSnap.data();
    
    // Verify the user owns this order
    if (parentOrderData.userId !== user.uid) {
      throw new Error('Not authorized to cancel this order');
    }

    // Check if order can be cancelled (only PLACED orders can be cancelled by customer)
    console.log('🔍 Parent order status check:', { 
      status: parentOrderData.status, 
      expected: 'PLACED',
      canCancel: parentOrderData.status === 'PLACED' || parentOrderData.status === 'placed'
    });
    
    if (parentOrderData.status !== 'PLACED' && parentOrderData.status !== 'placed') {
      throw new Error('This order cannot be cancelled. Only orders with PLACED status can be cancelled.');
    }

    // Get the merchant order for this group
    const merchantOrderQuery = query(
      merchantOrdersCollection,
      where('parentOrderId', '==', parentOrderId),
      where('sellerId', '==', groupSellerId)
    );

    const merchantOrderSnapshot = await getDocs(merchantOrderQuery);
    
    if (merchantOrderSnapshot.empty) {
      throw new Error('No merchant order found for this group');
    }

    const merchantOrderId = merchantOrderSnapshot.docs[0].id;

    // Get all order items for this group
    const orderItemsQuery = query(
      orderItemsCollection,
      where('merchantOrderId', '==', merchantOrderId),
      where('sellerId', '==', groupSellerId),
      where('serviceDate', '==', serviceDate)
    );

    const orderItemsSnapshot = await getDocs(orderItemsQuery);
    
    if (orderItemsSnapshot.empty) {
      throw new Error('No items found for this group');
    }

    // Update merchant order and items
    const batch = writeBatch(db);
    
    // Update merchant order
    batch.update(merchantOrderSnapshot.docs[0].ref, {
      status: 'canceled',
      cancellationReason: reason,
      cancelledAt: serverTimestamp(),
      cancelledBy: 'customer',
      updatedAt: serverTimestamp(),
      processing: true  // Prevent duplicate Cloud Function processing
    });

    // Update all items in the group
    orderItemsSnapshot.docs.forEach(itemDoc => {
      batch.update(itemDoc.ref, {
        status: 'cancelled',
        cancelled: true,
        cancellationReason: reason,
        cancelledAt: serverTimestamp(),
        cancelledBy: 'customer',
        updatedAt: serverTimestamp()
      });
    });

    // Commit all updates
    await batch.commit();

    console.log(`✅ Customer order group ${groupId} cancelled successfully`);
    
    return { 
      success: true, 
      message: 'Order group cancelled successfully',
      cancelledItems: orderItemsSnapshot.size
    };

  } catch (error) {
    console.error('❌ Error cancelling customer order group:', error);
    throw error;
  }
}
